import React from 'react';
import { Spinner } from '@/components/uiKit/Spinner';
import { useParams } from 'react-router-dom';
import { useFetchResource, useQueryNavigation } from '@/hooks';
import { navigateToAssetComponents } from '@/utils/shared';
import { ASSET_STUDIO, FILTER_PARAMS, SENSOR_DATA_INNER_TABS } from '@/types';
import { BaseButton } from '@/components/uiKit/Button';
import { DMVideoPlayer } from '@/components/shared/media/DMVideoPlayer';

interface EvidenceImageModalProps {
  resourceId: string;
  evidenceText?: string;
  assetId?: string;
  componentId?: string;
  showGoToDataStudio?: boolean;
  hasCamera?: boolean;
  onClose?: () => void;
}

export const EvidenceImageModal = ({
  resourceId,
  evidenceText,
  assetId,
  componentId,
  showGoToDataStudio = true,
  hasCamera = false,
  onClose,
}: EvidenceImageModalProps) => {
  const { siteId } = useParams();
  const { navigateWithQuery } = useQueryNavigation();
  const { resource, resourceType, isLoadingResource, isError, retry } = useFetchResource(resourceId);

  const isVideo = resourceType?.startsWith('video');

  const handleNavigate = () => {
    if (siteId && assetId && componentId) {
      navigateToAssetComponents({
        siteId,
        assetId,
        componentId,
        navigateWithQuery,
        replaceParams: {
          drawer: null,
          itemId: null,
        },
        addParams: hasCamera
          ? {
              [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.VISUAL_AI,
              ...(resourceId && { [FILTER_PARAMS.EVIDENCE_RESOURCE_ID]: resourceId }),
            }
          : {
              [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
              [FILTER_PARAMS.TAB]: SENSOR_DATA_INNER_TABS.SENSOR_FUSION,
            },
      });
      onClose?.();
    }
  };

  return (
    <div className="h-full w-full flex flex-col gap-2 ">
      {/* Evidence Text */}
      {evidenceText && <div className="text-sm text-[#55687D]">{evidenceText}</div>}

      {/* Evidence Content */}
      <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
        {isLoadingResource ? (
          <Spinner size="large" />
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 text-[#55687D]">
            <span>Failed to load evidence</span>
            <BaseButton variant="outline" onClick={retry}>
              Try Again
            </BaseButton>
          </div>
        ) : resource ? (
          isVideo ? (
            <DMVideoPlayer src={resource} light={false} />
          ) : (
            <img src={resource} className="object-contain h-full max-w-full" alt="Evidence" />
          )
        ) : (
          <div className="text-[#55687D]">Data not found</div>
        )}
      </div>

      {/* Footer with navigation button */}
      {showGoToDataStudio && assetId && componentId && (
        <div className="flex justify-end pt-4">
          <BaseButton variant="filled" onClick={handleNavigate}>
            {hasCamera ? 'Go to Visual AI' : 'Go to Data Studio'}
          </BaseButton>
        </div>
      )}
    </div>
  );
};
